import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditDisplayService } from '../../../shared/services/audit-display.service';
import { PriceListApiService } from '../services/price-list-api.service';
import {
  PriceListResponse,
  PriceListItemResponse,
} from '../models/price-list.model';
import { CurrencyResponse, ProductResponse } from '../models/producto.model';
import { ProductoApiService } from '../services/producto-api.service';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { AuthService } from '../../auth-pages/services/auth.service';
import { currencyFractionDigits } from '../../../shared/utils/currency-format.utils';

@Component({
  selector: 'app-price-list-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    BadgeComponent,
    AuditTimelineComponent,
  ],
  templateUrl: './price-list-detail-modal.component.html',
})
export class PriceListDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() priceList: PriceListResponse | null = null;
  @Input() currencies: CurrencyResponse[] = [];
  @Output() close = new EventEmitter<void>();

  items: PriceListItemResponse[] = [];
  loadingItems = false;

  // Audit timeline
  activeTab: 'info' | 'timeline' = 'info';
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  private productsById = new Map<number, ProductResponse>();
  businessId = 1;

  constructor(
    private priceListApi: PriceListApiService,
    private productApi: ProductoApiService,
    private datetimeService: DatetimeService,
    private auditApi: AuditApiService,
    private auditDisplay: AuditDisplayService,
    private authService: AuthService,
  ) {
    // Inc 58: businessId real del usuario (el 1 fijo provocaba 403 en otros negocios).
    this.businessId = this.authService.getBusinessId() ?? this.businessId;
  }

  /**
   * Inc 58: reacciona también a un cambio de `priceList` con el modal ya abierto y
   * resetea los flags de carga, que antes podían quedar colgados en true.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!(changes['isOpen'] || changes['priceList'])) return;
    if (!this.isOpen || !this.priceList) return;

    this.businessId = this.authService.getBusinessId() ?? this.businessId;
    this.activeTab = 'info';
    this.items = [];
    this.loadingItems = false;
    this.timelineLogs = [];
    this.timelinePage = 0;
    this.cargarItems();
  }

  onClose(): void {
    this.close.emit();
  }

  private cargarItems(): void {
    if (!this.priceList) return;
    this.loadingItems = true;
    this.priceListApi.getPriceListItems(this.priceList.id).subscribe({
      next: (data) => {
        this.items = data;
        this.loadingItems = false;
        this.cargarDatosProductos();
      },
      error: () => {
        this.items = [];
        this.loadingItems = false;
      },
    });
  }

  private cargarDatosProductos(): void {
    const idsUnicos = [...new Set(this.items.map((it) => it.productId))];
    idsUnicos.forEach((id) => {
      this.productApi.getProductById(id, this.businessId).subscribe({
        next: (product) => this.productsById.set(id, product),
        error: () => {},
      });
    });
  }

  getProductName(productId: number): string {
    return this.productsById.get(productId)?.name ?? `Producto #${productId}`;
  }

  getProductSku(productId: number): string {
    return this.productsById.get(productId)?.sku ?? '';
  }

  onTabChange(tab: 'info' | 'timeline'): void {
    this.activeTab = tab;
    if (
      tab === 'timeline' &&
      this.timelineLogs.length === 0 &&
      !this.loadingTimeline
    ) {
      this.loadTimeline(0);
    }
  }

  loadTimeline(page = 0): void {
    if (!this.priceList?.id) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;

    this.auditApi
      .getLogs({
        entityName: 'product_price_list',
        idEntity: String(this.priceList.id),
        page,
        size: 15,
      })
      .subscribe({
        next: (data) => {
          this.timelineLogs = data.content ?? [];
          this.timelinePage = data.page ?? page;
          this.timelineTotalPages = data.totalPages ?? 1;
          this.timelineTotalElements = data.totalElements ?? 0;
          this.loadingTimeline = false;
        },
        error: () => {
          this.timelineError = 'Error al cargar la línea de tiempo.';
          this.loadingTimeline = false;
        },
      });
  }

  getLogDotColor(op: string): string {
    switch (op?.toUpperCase()) {
      case 'CREATE':
        return 'bg-success-500';
      case 'UPDATE':
        return 'bg-brand-500';
      case 'DELETE':
        return 'bg-error-500';
      default:
        return 'bg-gray-400';
    }
  }

  getCurrencyCode(currencyId?: number): string {
    if (!currencyId) return '-';
    const c = this.currencies.find((x) => x.id === currencyId);
    return c?.code || `#${currencyId}`;
  }

  formatPrecio(price?: number, currencyCode?: string): string {
    if (price === undefined || price === null) return '-';
    const code = currencyCode || 'USD';
    try {
      // Sin minimumFractionDigits: Intl ya usa la unidad mínima ISO 4217 de la
      // moneda (0 en JPY, 2 en USD, 3 en KWD). Forzar 2 pintaba importes
      // inexistentes en las monedas sin céntimos.
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: code,
      }).format(price);
    } catch {
      return `${code} ${price.toFixed(currencyFractionDigits(code))}`;
    }
  }

  formatDate(dateStr?: string): string {
    return this.datetimeService.formatDateTime(dateStr);
  }

  //timeline detalles de cambios

  private readonly BOOLEAN_FIELDS = ['isDefault', 'isActive'];
  private readonly PERCENT_FIELDS = ['discountPercent'];
  private readonly MONEY_FIELDS = ['price', 'resolvedPrice', 'discountAmount'];

  private readonly PRICE_TYPE_LABELS: Record<string, string> = {
    fixed: 'Precio fijo',
    discount_percent: 'Descuento %',
    discount_amount: 'Descuento monto',
  };

  private readonly FIELD_LABELS: Record<string, string> = {
    name: 'Nombre',
    description: 'Descripción',
    currencyId: 'Moneda',
    isDefault: 'Predeterminada',
    isActive: 'Estado activo',
    discountPercent: 'Descuento %',
    priceType: 'Tipo de precio',
    price: 'Precio',
    resolvedPrice: 'Precio resuelto',
    minQuantity: 'Cantidad mínima',
  };

  private readonly HIDDEN_FIELDS = [
    'id',
    'businessId',
    'datetimeCreated',
    'datetimeUpdated',
    'totalItems',
  ];

  getUserLabel(log: AuditLogResponse): string {
    if (log.userFullName) return log.userFullName;
    if (log.username) return log.username;
    return `Usuario #${log.idUser}`;
  }

  getChangedValues(
    log: AuditLogResponse,
  ): Array<{ field: string; label: string; old: string; new: string }> {
    // Preferir el enriquecimiento del backend (changesDisplay); si es null, fallback técnico propio.
    const enriched = this.auditDisplay.displayRows(log);
    if (enriched !== null) {
      return enriched.map((r) => ({
        field: r.field,
        label: r.label,
        old: r.oldVal,
        new: r.newVal,
      }));
    }
    try {
      const oldObj =
        typeof log.oldValues === 'string'
          ? JSON.parse(log.oldValues)
          : log.oldValues || {};
      const newObj =
        typeof log.newValues === 'string'
          ? JSON.parse(log.newValues)
          : log.newValues || {};

      const fields: string[] =
        Array.isArray(log.changedFields) && log.changedFields.length > 0
          ? log.changedFields
          : Object.keys(newObj).filter(
              (k) => JSON.stringify(oldObj[k]) !== JSON.stringify(newObj[k]),
            );

      return fields
        .filter((f) => !this.HIDDEN_FIELDS.includes(f))
        .map((f) => ({
          field: f,
          label: this.FIELD_LABELS[f] || f,
          old: this.formatFieldValue(f, oldObj[f]),
          new: this.formatFieldValue(f, newObj[f]),
        }))
        .filter((f) => f.old !== f.new);
    } catch {
      return [];
    }
  }

  getCurrencyCodeById(currencyId?: number): string {
    if (!currencyId) return 'USD';
    const c = this.currencies.find((x) => x.id === currencyId);
    return c?.code || 'USD';
  }

  private formatFieldValue(field: string, value: unknown): string {
    if (value === undefined || value === null || value === '') return '-';

    if (this.BOOLEAN_FIELDS.includes(field) || typeof value === 'boolean') {
      return value === true || value === 'true' ? 'Sí' : 'No';
    }
    if (field === 'currencyId') return this.getCurrencyCode(Number(value));
    if (field === 'priceType')
      return this.PRICE_TYPE_LABELS[String(value)] ?? String(value);
    if (this.PERCENT_FIELDS.includes(field)) return `${value}%`;
    if (this.MONEY_FIELDS.includes(field))
      return this.formatPrecio(Number(value));

    return String(value);
  }

  //helpers para el detalle
  getPriceTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      fixed: 'Precio fijo',
      discount_percent: 'Descuento %',
      discount_amount: 'Descuento monto',
    };
    return labels[type] ?? type;
  }

  private convertirMonto(
    monto: number,
    monedaOrigenCode: string,
    monedaDestinoCode: string,
  ): number {
    if (!monto || monedaOrigenCode === monedaDestinoCode) return monto;

    const tasaOrigen = this.obtenerRateUSD(monedaOrigenCode);
    const tasaDestino = this.obtenerRateUSD(monedaDestinoCode);

    if (!tasaOrigen || !tasaDestino) return monto;

    const montoEnUSD = monto / tasaOrigen;
    return montoEnUSD * tasaDestino;
  }

  private obtenerRateUSD(monedaCode: string): number | null {
    const currency = this.currencies.find((c) => c.code === monedaCode);
    if (!currency?.rateUsd) return null;
    const rate = Number(currency.rateUsd);
    return isNaN(rate) || rate <= 0 ? null : rate;
  }

  /** Precio base: SIN convertir, en la moneda propia del producto. */
  getProductBasePrice(productId: number): number | undefined {
    return this.productsById.get(productId)?.price;
  }

  /** Moneda propia del producto (para etiquetar "Precio base" correctamente). */
  getProductCurrencyCode(productId: number): string {
    return this.productsById.get(productId)?.currencyCode || 'USD';
  }

  /**
   * Precio lista: SIEMPRE convertido a la moneda fija de la lista.
   * Se calcula localmente porque it.resolvedPrice no llega del backend.
   */
  getResolvedPrice(it: PriceListItemResponse): number | undefined {
    const producto = this.productsById.get(it.productId);
    if (!producto) return undefined;

    const monedaDestino = this.getCurrencyCodeById(this.priceList?.currencyId);
    const baseConvertida = this.convertirMonto(
      producto.price,
      producto.currencyCode ?? 'USD',
      monedaDestino,
    );

    switch (it.priceType) {
      case 'fixed':
        return it.price ?? baseConvertida;
      case 'discount_percent':
        return (
          baseConvertida - (baseConvertida * (it.discountPercent ?? 0)) / 100
        );
      case 'discount_amount': {
        const descuentoConvertido = this.convertirMonto(
          it.discountAmount ?? 0,
          producto.currencyCode ?? 'USD',
          monedaDestino,
        );
        return baseConvertida - descuentoConvertido;
      }
      default:
        return baseConvertida;
    }
  }

  /** Descuento por monto convertido a la moneda de la lista (para la columna "Config."). */
  convertirMontoDescuento(it: PriceListItemResponse): number | undefined {
    if (it.discountAmount === undefined || it.discountAmount === null) {
      return undefined;
    }
    const producto = this.productsById.get(it.productId);
    const monedaOrigen = producto?.currencyCode ?? 'USD';
    const monedaDestino = this.getCurrencyCodeById(this.priceList?.currencyId);
    return this.convertirMonto(it.discountAmount, monedaOrigen, monedaDestino);
  }

  /** Total mínimo: precio lista (convertido) × cantidad mínima. */
  getItemTotal(it: PriceListItemResponse): number | undefined {
    const resolved = this.getResolvedPrice(it);
    if (resolved === undefined || resolved === null) return undefined;
    const qty = it.minQuantity && it.minQuantity > 0 ? it.minQuantity : 1;
    return resolved * qty;
  }
}
